import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Icon, Layout, Page, Text } from '@loadsmart/miranda-react'

import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import {
  buildDemoIntegrations,
  demoIntegrationsLastRefresh,
  formatDemoIntegrationDateTime,
  DEMO_INTEGRATION_HIGHLIGHTS,
  DEMO_INTEGRATION_TIMEZONES,
  type DemoIntegrationTimezone,
} from 'managed-analytics/domain/demoIntegrations'
import {
  StyledActions,
  StyledCallout,
  StyledCalloutMarker,
  StyledCell,
  StyledCellMuted,
  StyledClosingAction,
  StyledClosingBand,
  StyledClosingBody,
  StyledClosingCopy,
  StyledClosingTitle,
  StyledContentColumn,
  StyledDisclaimer,
  StyledEyebrow,
  StyledHeadline,
  StyledHeadlineSoft,
  StyledHeaderCell,
  StyledHighlight,
  StyledHighlightGrid,
  StyledLede,
  StyledPanel,
  StyledPanelBadge,
  StyledPanelHeader,
  StyledPanelMeta,
  StyledPanelTitle,
  StyledRow,
  StyledSection,
  StyledSegment,
  StyledSegmented,
  StyledStatusPill,
  StyledTable,
  StyledTableScroller,
} from 'managed-analytics/ui/components/demoFeature/DemoFeature.styles'

/**
 * The Integrations preview, at /demo/integrations. A table of green rows
 * explains nothing on its own, so the sample carries one disrupted feed.
 */

const TRACK_EVENT_EXPIRY_MS = 1 * 60 * 1000

const LAST_TRACKED_STORAGE_KEY = 'lastTrackedDemoIntegrationsEvent'

const LEAD_TITLE = 'Talk to our team'

// One ask, so one label and one dialog title. Only the Mixpanel source
// differs, which is how we learn which half of the page convinces.
type LeadIntent = {
  source: string
  description: string
}

const HERO_LEAD: LeadIntent = {
  source: 'Hero',
  description:
    'Tell us where to reach you and we will walk through the feeds behind your reports — what we monitor, and what you would see here.',
}

const CLOSING_LEAD: LeadIntent = {
  source: 'Closing band',
  description:
    'Tell us how your data reaches us today. We will get back to you about putting these feeds under monitoring.',
}

export default function DemoIntegrationsPage() {
  const { track } = useAnalytics()

  const [timezone, setTimezone] = useState<DemoIntegrationTimezone>('UTC')
  const [leadIntent, setLeadIntent] = useState<LeadIntent | null>(null)
  const [isLeadDialogOpen, setIsLeadDialogOpen] = useState(false)

  // Pinned on mount so a moving clock does not shuffle the rows between renders.
  const now = useMemo(() => new Date(), [])
  const integrations = useMemo(() => buildDemoIntegrations(now), [now])
  const disruptedIntegration = integrations.find(item => item.status === 'Disrupt')

  useEffect(() => {
    function shouldTrackEvent() {
      const lastTracked = localStorage.getItem(LAST_TRACKED_STORAGE_KEY)
      if (!lastTracked) return true

      return Date.now() - parseInt(lastTracked, 10) > TRACK_EVENT_EXPIRY_MS
    }

    if (shouldTrackEvent()) {
      track(AnalyticsEvent.OpenDemoIntegrations)
      localStorage.setItem(LAST_TRACKED_STORAGE_KEY, Date.now().toString())
    }
  }, [track])

  const openLeadForm = useCallback(
    (intent: LeadIntent) => {
      track(AnalyticsEvent.OpenDemoIntegrationsLeadForm, { Source: intent.source })
      setLeadIntent(intent)
      setIsLeadDialogOpen(true)
    },
    [track]
  )

  const changeTimezone = useCallback(
    (nextTimezone: DemoIntegrationTimezone) => {
      setTimezone(nextTimezone)
      track(AnalyticsEvent.DemoIntegrationsChangeTimezone, { Timezone: nextTimezone })
    },
    [track]
  )

  return (
    <AppLayout>
      <Header
        unauthCtaLabel="Sign in"
        unauthCtaHref="/"
        title={
          <Text color="color-text-primary" variant="body-md-bold">
            Integrations
          </Text>
        }
      />
      <Page.Content>
        <Page.Body>
          <StyledContentColumn gap="spacing-12">
            <StyledSection gap="spacing-5">
              <StyledEyebrow>
                <Icon name="signal" size="14px" />
                Included with your Loadsmart reporting
              </StyledEyebrow>
              <StyledHeadline>
                A report is only as current as the feed behind it.{' '}
                <StyledHeadlineSoft>See whether today’s data landed.</StyledHeadlineSoft>
              </StyledHeadline>
              <StyledLede>
                Your numbers arrive from a chain of feeds — a nightly TMS drop, a warehouse export
                that comes by email, a workbook someone still updates by hand. The Integrations
                Overview watches every one of them and tells you, before you open a report, whether
                it was built on data that arrived on time.
              </StyledLede>
              <StyledActions>
                <Button
                  variant="primary"
                  leading={<Icon name="send" />}
                  onClick={() => openLeadForm(HERO_LEAD)}
                >
                  {LEAD_TITLE}
                </Button>
              </StyledActions>
            </StyledSection>

            <StyledSection gap="spacing-3">
              <StyledPanel>
                <StyledPanelHeader>
                  <StyledPanelTitle>Integrations Overview</StyledPanelTitle>
                  <StyledPanelBadge>Sample data</StyledPanelBadge>
                  <StyledPanelMeta>
                    Last refresh{' '}
                    {formatDemoIntegrationDateTime(demoIntegrationsLastRefresh(now), timezone)}
                  </StyledPanelMeta>
                  <StyledSegmented role="group" aria-label="Timezone">
                    {DEMO_INTEGRATION_TIMEZONES.map(zone => (
                      <StyledSegment
                        key={zone}
                        type="button"
                        $active={zone === timezone}
                        aria-pressed={zone === timezone}
                        onClick={() => changeTimezone(zone)}
                      >
                        {zone}
                      </StyledSegment>
                    ))}
                  </StyledSegmented>
                </StyledPanelHeader>

                <StyledTableScroller>
                  <StyledTable>
                    <thead>
                      <tr>
                        <StyledHeaderCell>Name</StyledHeaderCell>
                        <StyledHeaderCell>Type</StyledHeaderCell>
                        <StyledHeaderCell>Usage</StyledHeaderCell>
                        <StyledHeaderCell>Schedule</StyledHeaderCell>
                        <StyledHeaderCell>Trigger ({timezone})</StyledHeaderCell>
                        <StyledHeaderCell>Deadline ({timezone})</StyledHeaderCell>
                        <StyledHeaderCell>Last run ({timezone})</StyledHeaderCell>
                        <StyledHeaderCell $align="center">Status</StyledHeaderCell>
                      </tr>
                    </thead>
                    <tbody>
                      {integrations.map(integration => (
                        <StyledRow
                          key={integration.id}
                          $highlight={integration.status === 'Disrupt'}
                        >
                          <StyledCell>{integration.name}</StyledCell>
                          <StyledCell>
                            <StyledCellMuted>{integration.type}</StyledCellMuted>
                          </StyledCell>
                          <StyledCell>
                            <StyledCellMuted>{integration.usage}</StyledCellMuted>
                          </StyledCell>
                          <StyledCell>
                            <StyledCellMuted>{integration.cronDescription}</StyledCellMuted>
                          </StyledCell>
                          <StyledCell>
                            {formatDemoIntegrationDateTime(integration.trigger, timezone)}
                          </StyledCell>
                          <StyledCell>
                            {formatDemoIntegrationDateTime(integration.deadline, timezone)}
                          </StyledCell>
                          <StyledCell>
                            {formatDemoIntegrationDateTime(integration.lastRun, timezone)}
                          </StyledCell>
                          <StyledCell $align="center">
                            <StyledStatusPill
                              $tone={integration.status === 'Disrupt' ? 'danger' : 'success'}
                            >
                              {integration.status}
                            </StyledStatusPill>
                          </StyledCell>
                        </StyledRow>
                      ))}
                    </tbody>
                  </StyledTable>
                </StyledTableScroller>

                {disruptedIntegration && (
                  <StyledCallout>
                    <StyledCalloutMarker>
                      <Icon name="attention" size="16px" />
                    </StyledCalloutMarker>
                    <span>
                      <strong>{disruptedIntegration.name}</strong> missed its deadline — the last
                      file landed yesterday. Any accessorial figure you read today is a day behind,
                      and you know that before you quote it in a meeting rather than after someone
                      questions the number.
                    </span>
                  </StyledCallout>
                )}
              </StyledPanel>

              <StyledDisclaimer>
                <Icon name="info" size="14px" />
                Illustrative data. Switch the timezone to see the same behaviour as the signed-in
                overview.
              </StyledDisclaimer>
            </StyledSection>

            <StyledSection gap="spacing-5">
              <Layout.Stack gap="spacing-1">
                <Text variant="heading-lg-bold" color="color-text-primary">
                  What the overview answers
                </Text>
                <Text variant="body-md" color="color-text-tertiary">
                  Four questions you would otherwise answer by emailing someone.
                </Text>
              </Layout.Stack>

              <StyledHighlightGrid>
                {DEMO_INTEGRATION_HIGHLIGHTS.map(highlight => (
                  <StyledHighlight key={highlight.title}>
                    <Text variant="body-md-bold" color="color-text-primary">
                      {highlight.title}
                    </Text>
                    <Text variant="body-sm" color="color-text-secondary">
                      {highlight.body}
                    </Text>
                  </StyledHighlight>
                ))}
              </StyledHighlightGrid>
            </StyledSection>

            <StyledClosingBand>
              <StyledClosingCopy>
                <StyledClosingTitle>Which of your feeds would you put here first?</StyledClosingTitle>
                <StyledClosingBody>
                  Tell us how your freight and dock data reaches us today. We will come back with
                  what monitoring it looks like on your account.
                </StyledClosingBody>
              </StyledClosingCopy>
              <StyledClosingAction type="button" onClick={() => openLeadForm(CLOSING_LEAD)}>
                <Icon name="send" size="14px" />
                {LEAD_TITLE}
              </StyledClosingAction>
            </StyledClosingBand>
          </StyledContentColumn>
        </Page.Body>
      </Page.Content>

      {leadIntent && (
        <ZendeskDialog
          requestType={RequestType.TryItNow}
          title={LEAD_TITLE}
          description={leadIntent.description}
          open={isLeadDialogOpen}
          onClose={() => setIsLeadDialogOpen(false)}
        />
      )}
    </AppLayout>
  )
}
